const expectsArray = metadata =>
	metadata?.type === 'array' || metadata?.multiple;
const emptyMetadata = Object.freeze({});

const legacyFilterKeys = [
	'projectsParam',
	'entityParam',
	'warehouseParam',
	'itemParam',
];

export const getReportMetadata = record => ({
	reportParams: record?.reportParams || record?.params || emptyMetadata,
	reportProps: record?.reportProps || record?.props || emptyMetadata,
});

export const hasReportFilterComponent = (components = [], param) =>
	components.some(
		component =>
			component.param === param || component.config?.paramAlias === param
	);

export const getLegacyReportFilterKeys = (components = [], reportParams = {}) =>
	legacyFilterKeys.filter(
		param => reportParams[param] && !hasReportFilterComponent(components, param)
	);

export const buildReportParams = (components = [], reportParams = {}) => {
	const componentParams = components.reduce((acc, component) => {
		const result = { ...acc, ...component.params };
		if (component.param && component.type !== 'submit') {
			result[component.param] = {
				...result[component.param],
				...component.config,
				type: component.type,
			};
		}
		return result;
	}, {});

	// The SQL report contract remains authoritative when a form field shares its key.
	return Object.keys(reportParams).reduce(
		(acc, param) => ({
			...acc,
			[param]: { ...acc[param], ...reportParams[param] },
		}),
		componentParams
	);
};

export const getDefaultReportProjects = (
	reportParams = {},
	selectedProjectId
) => {
	const metadata = reportParams.projectsParam;
	if (!metadata) return undefined;
	if (Array.isArray(metadata.defaultValues) && metadata.defaultValues.length)
		return metadata.defaultValues;
	const projectId = Number(selectedProjectId);
	return Number.isSafeInteger(projectId) && projectId > 0
		? [projectId]
		: undefined;
};

export const isCardexProjectReport = (
	reportId,
	components = [],
	reportParams = {}
) =>
	Number(reportId) === 3 &&
	reportParams.projectsParam?.type === 'array' &&
	reportParams.itemParam?.type === 'array' &&
	components.some(component => component.config?.paramAlias === 'itemParam') &&
	components.some(
		component => component.config?.paramAlias === 'warehouseParam'
	);

export const isReportProjectBoundToFeed = (
	components = [],
	reportParams = {},
	selectedProjectId
) =>
	!!reportParams.projectsParam?.required &&
	!!reportParams.itemParam &&
	components.some(component => component.config?.paramAlias === 'itemParam') &&
	Number.isSafeInteger(Number(selectedProjectId)) &&
	Number(selectedProjectId) > 0;

export const resetReportScopeValues = ({
	values,
	components = [],
	reportParams = {},
	selectedProjectId,
	reportId,
}) => {
	const selectionParams = new Set([
		...legacyFilterKeys.filter(param => reportParams[param]),
		...components
			.filter(component => component.type === 'list')
			.map(component => component.param),
	]);
	const result = { ...values };
	for (const param of selectionParams) {
		if (param && !param.startsWith('selected')) result[param] = undefined;
	}
	if (isCardexProjectReport(reportId, components, reportParams))
		result.projectsParam = getDefaultReportProjects(
			reportParams,
			selectedProjectId
		);
	else if (
		isReportProjectBoundToFeed(components, reportParams, selectedProjectId)
	)
		result.projectsParam = [Number(selectedProjectId)];
	return result;
};

export const buildGenerateReportPayload = ({
	reportId,
	reportTable,
	values = {},
	reportParams = {},
	components = [],
}) => {
	const { toEmails, ccEmails, subject, message, ...params } = values;

	for (const component of components) {
		const alias = component.config?.paramAlias;
		if (
			alias &&
			Object.prototype.hasOwnProperty.call(reportParams, alias) &&
			params[alias] === undefined &&
			params[component.param] !== undefined
		) {
			params[alias] = params[component.param];
		}
	}

	if (
		!isCardexProjectReport(reportId, components, reportParams) &&
		isReportProjectBoundToFeed(
			components,
			reportParams,
			values.selectedProjectId
		)
	) {
		// Item options use the Feed business line; keep their project aligned.
		params.projectsParam = [Number(values.selectedProjectId)];
	} else if (
		params.projectsParam === undefined &&
		!hasReportFilterComponent(components, 'projectsParam')
	) {
		const projects = getDefaultReportProjects(
			reportParams,
			values.selectedProjectId
		);
		if (projects !== undefined) params.projectsParam = projects;
	}

	for (const [param, metadata] of Object.entries(reportParams)) {
		const value = params[param];
		if (
			expectsArray(metadata) &&
			value !== undefined &&
			value !== null &&
			!Array.isArray(value)
		) {
			params[param] = [value];
		}
	}

	return {
		reportId,
		reportTable,
		toEmails,
		ccEmails,
		subject,
		message,
		params,
	};
};

export const filterReportOptionIds = (value, options = []) =>
	(Array.isArray(value) ? value : []).filter(id =>
		options.some(option => String(option.id) === String(id))
	);
